import { set } from 'lodash-es';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  BaseSchedulerHighlightValue,
  createSchedulerFiltersStore,
  createSchedulerHighlightStore,
} from '@lp/core/store/scheduler';
import { createSelectors } from '@/utils/helpers';

type RegionConfig = {
  isExpanded?: boolean;
  totalsOpened?: boolean;
};

type HubConfig = {
  isExpanded?: boolean;
  totalsOpened?: boolean;
  insertedSpecialists?: string[];
};

type CSScheduleState = {
  regionConfigs: Record<string, RegionConfig>;
  hubConfigs: Record<string, HubConfig>;
};

type CSScheduleActions = {
  setRegionExpand: (regionId: string, open?: boolean) => void;
  setRegionTotalsOpen: (regionId: string, open?: boolean) => void;
  setHubExpand: (hubId: string, open?: boolean) => void;
  setHubTotalsOpen: (hubId: string, open?: boolean) => void;
  insertHubSpecialists: (hubId: string, userIds: string[]) => void;
};

type CSScheduleStore = CSScheduleState & CSScheduleActions;

const useCSSchedulerStoreBase = create<CSScheduleStore>()(
  immer((setState) => ({
    regionConfigs: {},
    hubConfigs: {},
    setRegionExpand: (regionId, expand) => {
      setState((state) => {
        const newValue = expand === undefined ? !state.regionConfigs[regionId]?.isExpanded : expand;
        set(state.regionConfigs, [regionId, 'isExpanded'], newValue);
      });
    },
    setRegionTotalsOpen: (regionId, open) => {
      setState((state) => {
        const newValue = open === undefined ? !state.regionConfigs[regionId]?.totalsOpened : open;
        set(state.regionConfigs, [regionId, 'totalsOpened'], newValue);
      });
    },
    setHubExpand: (hubId, expand) => {
      setState((state) => {
        const newValue = expand === undefined ? !state.hubConfigs[hubId]?.isExpanded : expand;
        set(state.hubConfigs, [hubId, 'isExpanded'], newValue);
      });
    },
    setHubTotalsOpen: (hubId, open) => {
      setState((state) => {
        const newValue = open === undefined ? !state.hubConfigs[hubId]?.totalsOpened : open;
        set(state.hubConfigs, [hubId, 'totalsOpened'], newValue);
      });
    },
    insertHubSpecialists: (hubId, userIds) => {
      setState((state) => {
        const insertedIds = state.hubConfigs[hubId]?.insertedSpecialists ?? [];
        const newValue = Array.from(new Set([...insertedIds, ...userIds]));
        set(state.hubConfigs, [hubId, 'insertedSpecialists'], newValue);
      });
    },
  })),
);

const getRegionState = (id: string) => (store: CSScheduleStore) => store.regionConfigs[id] ?? {};
const getHubState = (id: string) => (store: CSScheduleStore) => store.hubConfigs[id] ?? {};

export const csSchedulerSelectors = {
  getRegionState,
  getHubState,
};

export const useCSSchedulerStore = createSelectors(useCSSchedulerStoreBase);

export const useCSSchedulerFiltersStore = createSchedulerFiltersStore();

type CSSchedulerHighlightValue = BaseSchedulerHighlightValue & {
  hubId: string;
};

export const useCSSchedulerHighlightStore = createSchedulerHighlightStore<CSSchedulerHighlightValue>();
